import React from "react";
import { Container } from "react-bootstrap";
import Header from "../Includes/Header";
import Footer from "../Includes/Footer";
import "../../App.css";

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Header />
      <Container className="py-5 mt-4" style={{ maxWidth: 820 }}>
        <h1 className="fw-bold mb-1">Privacy Policy</h1>
        <p className="text-secondary small mb-1">Last updated: August 2026</p>
        <p className="text-secondary small mb-5" style={{ lineHeight: 1.7 }}>
          ARM Financial Services Pty Ltd ("ARMF", "we", "us", or "our") · ABN 35 659 595 570 · ACN 659 595 570
          <br />
          Registered with AUSTRAC to provide remittance services in Australia (AAN 100812026, Registered Entity No. 75630)
          <br />
          Suite 110, Level 1, 530 Little Collins Street, Melbourne VIC 3000, Australia
        </p>

        <Section title="1. Introduction">
          <p>
            ARM Financial Services Pty Ltd ("ARMF", "we", "us", or "our") is committed to
            protecting your personal information in accordance with the Privacy Act 1988 (Cth)
            and the Australian Privacy Principles (APPs). This Privacy Policy explains how we
            collect, use, disclose, and safeguard your data when you use our onboarding platform
            and Australian remittance services. Please read this policy carefully. By using our
            platform, you consent to the practices described herein.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          We collect the following categories of personal information:
          <ul className="mt-2">
            <li><strong>Identity data:</strong> full name, date of birth, nationality, government-issued ID documents</li>
            <li><strong>Contact data:</strong> email address, phone number, residential address</li>
            <li><strong>Biometric data:</strong> facial images and liveness verification data collected during KYC</li>
            <li><strong>Financial data:</strong> account details, transaction history, source of funds</li>
            <li><strong>Technical data:</strong> IP address, browser type, device information, usage logs</li>
            <li><strong>Compliance data:</strong> PEP/sanctions screening results, AML risk scores</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          We use your personal information to:
          <ul className="mt-2">
            <li>Verify your identity and complete the KYC/AML onboarding process</li>
            <li>Open and manage your ARMF account</li>
            <li>Process payments and multi-currency transactions</li>
            <li>Comply with legal and regulatory obligations</li>
            <li>Detect, prevent, and investigate fraud and financial crime</li>
            <li>Communicate with you about your account and our services</li>
            <li>Send marketing communications (only with your consent)</li>
            <li>Improve our platform and services</li>
          </ul>
        </Section>

        <Section title="4. How We Are Permitted to Handle Your Information">
          <p>
            Our collection, use, and disclosure of your personal information is governed by the
            Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth). We collect and
            use your information because it is reasonably necessary for our functions and
            activities — principally, providing you with our remittance and payment services and
            meeting our obligations under the Anti-Money Laundering and Counter-Terrorism
            Financing Act 2006 (Cth) (the AML/CTF Act). Where we rely on your consent, such as for
            marketing communications, you may withdraw that consent at any time.
          </p>
        </Section>

        <Section title="5. Third-Party Service Providers">
          We may share your personal data with authorised third-party providers, including:
          <ul className="mt-2">
            <li>Identity verification providers (document OCR, biometric matching)</li>
            <li>Sanctions and PEP screening providers</li>
            <li>Fraud scoring services</li>
            <li>SMS and email delivery providers</li>
            <li>Cloud infrastructure and storage providers</li>
          </ul>
          All third-party providers are bound by data processing agreements and are only
          permitted to use your data for the specific purpose for which it was shared.
        </Section>

        <Section title="6. International Data Transfers">
          <p>
            Your data may be transferred to and processed in countries outside Australia,
            including by the third-party service providers listed in Section 5. Before doing so,
            we take reasonable steps, consistent with Australian Privacy Principle 8 (cross-border
            disclosure of personal information), to ensure the overseas recipient does not breach
            the APPs in relation to your information.
          </p>
        </Section>

        <Section title="7. Data Retention">
          <p>
            We retain your personal data for as long as necessary to provide our services and to
            comply with applicable legal, regulatory, and audit requirements. In accordance with
            the AML/CTF Act 2006 (Cth), KYC and AML records — including identity documents,
            transaction records, and screening results — are retained for a minimum of seven (7)
            years from the date the relevant transaction was conducted or the end of your
            relationship with ARMF, whichever is later.
          </p>
          <p>
            In some circumstances, we may be legally restricted from disclosing to you that
            certain information has been shared with, or requested by, AUSTRAC or another
            government agency.
          </p>
        </Section>

        <Section title="8. Your Rights">
          Depending on your jurisdiction, you may have the following rights regarding your
          personal data:
          <ul className="mt-2">
            <li><strong>Access:</strong> request a copy of the data we hold about you</li>
            <li><strong>Rectification:</strong> request correction of inaccurate data</li>
            <li><strong>Erasure:</strong> request deletion of your data (subject to legal retention obligations)</li>
            <li><strong>Restriction:</strong> request that we limit how we use your data</li>
            <li><strong>Portability:</strong> receive your data in a structured, machine-readable format</li>
            <li><strong>Objection:</strong> object to processing based on legitimate interests</li>
            <li><strong>Withdraw consent:</strong> withdraw consent for marketing at any time</li>
          </ul>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:privacy@armf.com">privacy@armf.com</a>.
        </Section>

        <Section title="9. Cookies & Tracking">
          <p>
            Our platform uses session storage and strictly-necessary cookies to maintain your
            onboarding session. We do not use third-party advertising or tracking cookies. See our{" "}
            <a href="/cookies-policy">Cookies Policy</a> for full details.
          </p>
        </Section>

        <Section title="10. Security">
          We implement industry-standard technical and organisational measures to protect your
          personal data against unauthorised access, disclosure, alteration, or destruction.
          These include encryption in transit (TLS), access controls, and regular security
          assessments. However, no method of transmission over the internet is 100% secure.
        </Section>

        <Section title="11. Children's Privacy">
          Our services are not directed at individuals under the age of 18. We do not knowingly
          collect personal data from minors. If you believe we have inadvertently collected such
          data, please contact us immediately.
        </Section>

        <Section title="12. Changes to This Policy">
          We may update this Privacy Policy from time to time. We will notify you of material
          changes by updating the "Last updated" date at the top of this page. We encourage you
          to review this policy periodically.
        </Section>

        <Section title="13. Complaints & Dispute Resolution">
          <p>
            If you have a concern about how we've handled your personal information, please
            contact our Privacy Officer at{" "}
            <a href="mailto:privacy@armf.com">privacy@armf.com</a> or +61 3 8679 2233. We will
            acknowledge your complaint promptly and aim to resolve it within 30 days.
          </p>
          <p>
            If you're not satisfied with our response, you may lodge a complaint with the{" "}
            <strong>Office of the Australian Information Commissioner (OAIC)</strong> at{" "}
            <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">oaic.gov.au</a>.
          </p>
          <p className="mb-0">
            For complaints about our remittance or payment services specifically, ARMF is a
            member of the <strong>Australian Financial Complaints Authority (AFCA)</strong>{" "}
            (Member No. FF157726). AFCA provides a free, independent dispute resolution service
            and can be contacted at{" "}
            <a href="https://www.afca.org.au" target="_blank" rel="noreferrer">afca.org.au</a>,
            on 1800 931 678, or by mail to GPO Box 3, Melbourne VIC 3001.
          </p>
        </Section>
      </Container>
      <Footer full={true} />
    </>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h5 className="fw-semibold mb-2">{title}</h5>
    <div className="text-secondary" style={{ lineHeight: 1.8 }}>{children}</div>
  </div>
);

export default PrivacyPolicy;
